import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronRight, CheckCircle, Clock, Flame, Mail, MessageCircle, Phone, RefreshCw, Send, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function CreditorReminders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creditor-reminders/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch { toast.error('Failed to load reminders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/creditor-reminders/send`, {}, { withCredentials: true });
      toast.success(`Notified ${res.data.clubs_notified} vendor(s) · ${res.data.emails_sent} email(s)`);
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const toggleUrgent = async (bill) => {
    try {
      await axios.patch(`${API}/purchases/bills/${bill.id}`, { urgent_pay: !bill.urgent_pay }, { withCredentials: true });
      toast.success(bill.urgent_pay ? 'Urgent flag removed' : 'Flagged urgent');
      load();
    } catch { toast.error('Failed'); }
  };

  const markPaid = async (bill) => {
    if (!window.confirm(`Mark INV ${bill.invoice_number || bill.id} as fully paid?`)) return;
    try {
      await axios.put(`${API}/purchases/bills/${bill.id}/payment`, {
        paid_amount: bill.total_amount,
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: 'Manual',
      }, { withCredentials: true });
      toast.success('Bill marked paid');
      load();
    } catch { toast.error('Failed'); }
  };

  const shareWhatsApp = async (club) => {
    if (!club.vendor_phone) { toast.error('No phone number on vendor record'); return; }
    try {
      const res = await axios.post(`${API}/creditor-reminders/whatsapp-preview`,
        { vendor: club.vendor, phone: club.vendor_phone }, { withCredentials: true });
      if (res.data.wa_url) window.open(res.data.wa_url, '_blank');
      else { navigator.clipboard.writeText(res.data.text); toast.success('Message copied — paste in WhatsApp'); }
    } catch { toast.error('Failed to compose'); }
  };

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading reminders…</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="creditor-reminders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Payment Reminders</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Bills due today, overdue and upcoming — clubbed by vendor within ±3 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:bg-zinc-800 transition-colors" data-testid="reminders-refresh">
            <RefreshCw size={14} />
          </button>
          <button onClick={runNow} disabled={sending} data-testid="reminders-run-btn"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs px-3 py-2 rounded-xl">
            {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />} Run Reminders Now
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Overdue" count={data.overdue.count} amount={data.overdue.total} color="red" icon={AlertTriangle} testid="kpi-overdue" />
        <KpiTile label="Pay Today" count={data.pay_today.count} amount={data.pay_today.total} color="amber" icon={Flame} testid="kpi-today" />
        <KpiTile label="Next 7 Days" count={data.upcoming_7d.count} amount={data.upcoming_7d.total} color="blue" icon={Clock} testid="kpi-upcoming" />
        <KpiTile label="7-Day Total" count={data.daily_totals.length} amount={data.grand_total_due_7d} color="zinc" icon={Calendar} testid="kpi-grand" isDays />
      </div>

      {/* Daily totals bar chart */}
      {data.daily_totals.length > 0 && (
        <DailyTotalsChart rows={data.daily_totals} />
      )}

      {/* Sections */}
      {['overdue', 'pay_today', 'upcoming_7d'].map(key => {
        const section = data[key];
        if (!section.count) return null;
        const titles = { overdue: 'Overdue', pay_today: 'Pay Today', upcoming_7d: 'Upcoming (next 7 days)' };
        const borderColor = { overdue: 'border-red-700/50', pay_today: 'border-amber-500/40', upcoming_7d: 'border-blue-700/40' }[key];
        return (
          <section key={key} className={`bg-zinc-900 border ${borderColor} rounded-2xl p-5`} data-testid={`section-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-zinc-50 text-sm font-semibold">{titles[key]} · {section.count} vendor{section.count > 1 ? 's' : ''} · {fmt(section.total)}</h3>
            </div>
            <div className="space-y-3">
              {section.clubs.map(club => (
                <VendorClubCard key={`${key}-${club.vendor}-${club.from_date}`} club={club} onToggleUrgent={toggleUrgent} onMarkPaid={markPaid} onShareWhatsApp={shareWhatsApp} />
              ))}
            </div>
          </section>
        );
      })}

      {!data.overdue.count && !data.pay_today.count && !data.upcoming_7d.count && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center">
          <CheckCircle size={32} className="text-green-400 mx-auto mb-3" />
          <div className="text-zinc-200 font-semibold">All caught up!</div>
          <div className="text-zinc-500 text-sm mt-1">No vendor payments are due in the next 7 days.</div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, count, amount, color, icon: Icon, testid, isDays }) {
  const colorClass = {
    red: 'bg-red-900/30 border-red-700/50 text-red-300',
    amber: 'bg-amber-900/30 border-amber-700/50 text-amber-300',
    blue: 'bg-blue-900/30 border-blue-700/50 text-blue-300',
    zinc: 'bg-zinc-800 border-zinc-700 text-zinc-300',
  }[color];
  return (
    <div className={`rounded-xl border p-4 ${colorClass}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-wider opacity-70">{isDays ? `${count} days` : `${count} vendor${count !== 1 ? 's' : ''}`}</span>
      </div>
      <div className="font-mono font-bold text-2xl text-zinc-50">{fmt(amount)}</div>
      <div className="text-[10px] opacity-80 mt-0.5">{label}</div>
    </div>
  );
}

function VendorClubCard({ club, onToggleUrgent, onMarkPaid, onShareWhatsApp }) {
  const [expanded, setExpanded] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const dateRange = club.from_date === club.to_date ? club.from_date : `${club.from_date} → ${club.to_date}`;
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`vendor-club-${club.vendor}`}>
      <button onClick={() => setExpanded(e => !e)} className="w-full flex items-center justify-between p-4 hover:bg-zinc-900/50 transition-colors text-left">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {club.urgent && <Flame size={13} className="text-red-400" />}
            <div className="text-zinc-50 font-semibold text-sm">{club.vendor}</div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{club.count} bill{club.count > 1 ? 's' : ''}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Due {dateRange}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-mono font-bold text-lg text-zinc-50">{fmt(club.total)}</div>
          <ChevronRight size={14} className={`text-zinc-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
          {/* Contact row */}
          <div className="flex flex-wrap gap-2">
            {club.vendor_phone && (
              <a href={`tel:${club.vendor_phone}`} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700">
                <Phone size={11} /> {club.vendor_phone}
              </a>
            )}
            {club.vendor_email && (
              <a href={`mailto:${club.vendor_email}`} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700">
                <Mail size={11} /> {club.vendor_email}
              </a>
            )}
            <button onClick={() => onShareWhatsApp(club)} data-testid={`wa-share-${club.vendor}`}
              className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-green-900/40 text-green-300 hover:bg-green-900/60">
              <MessageCircle size={11} /> Share on WhatsApp
            </button>
            <button onClick={() => setSettleOpen(true)} data-testid={`settle-all-${club.vendor}`}
              className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60">
              <CheckCircle size={11} /> Pay All {club.count} bill{club.count > 1 ? 's' : ''} ({fmt(club.total)})
            </button>
          </div>
          {/* Bills list */}
          <table className="w-full text-xs">
            <thead>
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
                <th className="text-left py-1.5">Invoice</th>
                <th className="text-left">Date</th>
                <th className="text-left">Due</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {club.bills.map(b => (
                <tr key={b.id} className="border-b border-zinc-800/50">
                  <td className="py-2 text-zinc-300 font-mono">{b.invoice_number || b.id}</td>
                  <td className="text-zinc-500">{b.invoice_date || '—'}</td>
                  <td className="text-zinc-400">{b.due_date || b._due || '—'}</td>
                  <td className="text-right text-zinc-100 font-mono">{fmt(b.outstanding)}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => onToggleUrgent(b)} data-testid={`toggle-urgent-${b.id}`}
                        title={b.urgent_pay ? 'Remove urgent flag' : 'Flag urgent'}
                        className={`px-2 py-1 rounded text-[10px] font-semibold ${b.urgent_pay ? 'bg-red-900/40 text-red-300' : 'bg-zinc-800 text-zinc-400 hover:text-red-300'}`}>
                        <Flame size={10} className="inline" /> {b.urgent_pay ? 'Urgent' : 'Mark Urgent'}
                      </button>
                      <button onClick={() => onMarkPaid(b)} data-testid={`mark-paid-${b.id}`}
                        className="px-2 py-1 rounded text-[10px] bg-green-900/40 text-green-300 hover:bg-green-900/60 font-semibold">
                        <CheckCircle size={10} className="inline" /> Paid
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {settleOpen && <SettleBillsModal club={club} onClose={() => setSettleOpen(false)} />}
    </div>
  );
}

function DailyTotalsChart({ rows }) {
  const max = Math.max(...rows.map(r => r.amount), 1);
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid="daily-totals-chart">
      <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-3">Daily Payment Load</h3>
      <div className="flex items-end gap-2 h-32">
        {rows.map(r => {
          const h = Math.max(8, (r.amount / max) * 100);
          return (
            <div key={r.date} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="w-full bg-amber-500/60 group-hover:bg-amber-400 rounded-t transition-colors relative" style={{ height: `${h}%` }}>
                <div className="opacity-0 group-hover:opacity-100 absolute -top-7 left-1/2 -translate-x-1/2 bg-zinc-950 border border-zinc-700 rounded px-2 py-0.5 text-[10px] text-zinc-200 whitespace-nowrap">
                  {fmt(r.amount)}
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 rotate-45 origin-left translate-x-2 whitespace-nowrap">{r.date.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SettleBillsModal({ club, onClose }) {
  const [selected, setSelected] = useState(() => Object.fromEntries(club.bills.map(b => [b.id, true])));
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('Bank');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const total = club.bills.filter(b => selected[b.id]).reduce((s, b) => s + b.outstanding, 0);
  const selectedCount = club.bills.filter(b => selected[b.id]).length;

  const submit = async () => {
    const bill_ids = Object.keys(selected).filter(id => selected[id]);
    if (!bill_ids.length) { toast.error('Tick at least one bill'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/creditor-reminders/bulk-pay`,
        { bill_ids, payment_date: payDate, payment_method: method, reference, notes },
        { withCredentials: true });
      toast.success(`Paid ${res.data.paid_count} bill(s) · ${fmt(res.data.total_paid)}`);
      onClose();
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-emerald-500/40 rounded-2xl w-full max-w-2xl p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="settle-bills-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-zinc-50 text-sm">Settle Multiple Bills · {club.vendor}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <table className="w-full text-[12px]">
          <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-800">
            <th className="text-left py-1">✓</th><th className="text-left py-1">Invoice</th>
            <th className="text-left py-1">Due</th><th className="text-right py-1">Amount</th>
          </tr></thead>
          <tbody>
            {club.bills.map(b => (
              <tr key={b.id} className="border-b border-zinc-800/50">
                <td className="py-2"><input type="checkbox" checked={!!selected[b.id]} onChange={e => setSelected(s => ({ ...s, [b.id]: e.target.checked }))} data-testid={`settle-chk-${b.id}`} /></td>
                <td className="text-zinc-300">{b.invoice_number || b.id}</td>
                <td className="text-zinc-500">{b.due_date || '—'}</td>
                <td className="text-right text-zinc-100 font-mono">{fmt(b.outstanding)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t-2 border-zinc-700">
            <td colSpan={3} className="py-2 text-zinc-400 text-right font-semibold">Total ({selectedCount} bill{selectedCount !== 1 ? 's' : ''})</td>
            <td className="py-2 text-right text-emerald-300 font-mono font-bold">{fmt(total)}</td>
          </tr></tfoot>
        </table>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase">Payment date</label>
            <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} data-testid="settle-date"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-50 text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase">Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)} data-testid="settle-method"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-50 text-xs">
              {['Bank', 'UPI', 'Cheque', 'NEFT', 'RTGS', 'IMPS', 'Cash'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Payment reference / UTR / cheque no (optional)"
          data-testid="settle-ref"
          className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-50 text-xs" />
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
          className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-50 text-xs" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs bg-zinc-800 text-zinc-300 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy || selectedCount === 0} data-testid="settle-submit"
            className="flex items-center gap-1.5 px-4 py-2 text-xs bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg font-semibold disabled:opacity-50">
            <CheckCircle size={12} /> {busy ? 'Settling…' : `Settle ${selectedCount} bill(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

